"use client";
import type { ReactNode } from "react";

import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";

import { $convertFromMarkdownString } from "@lexical/markdown";
import { $transformTextToMentionNodes } from "lexical-beautiful-mentions";
import { TRANSFORMERS } from "@lexical/markdown";

import {
  BeautifulMentionsPlugin,
  BeautifulMentionNode,
  type BeautifulMentionsTheme,
  type BeautifulMentionsMenuProps,
  type BeautifulMentionsMenuItemProps,
} from "lexical-beautiful-mentions";

import { Box, Text, Flex, Portal } from "@mantine/core";
import { forwardRef, useEffect, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, $createParagraphNode, $createTextNode } from "lexical";
import {
  transformOutputForDisplay,
  transformMentionItemsForDisplay,
} from "@/src/utils/output-template-transformers";

import styles from "@scss/custom-components/output.module.scss";

// Build a theme mapping for each trigger present in the mention items.
// This ensures styling applies for any trigger key (not only ":").
const buildBeautifulMentionsTheme = (
  items: Record<string, string[]>,
): BeautifulMentionsTheme => {
  const theme: Record<string, Record<string, string>> = {};
  Object.keys(items).forEach((trigger) => {
    theme[trigger] = {
      container: styles.mentionChip,
      containerFocused: styles.mentionChipFocused,
      trigger: styles.mentionTrigger,
      value: styles.mentionValue,
    };
  });
  return theme as BeautifulMentionsTheme;
};

function Menu(props: BeautifulMentionsMenuProps) {
  // Render the menu into a portal so it escapes parent stacking contexts.
  // Compute position from the current caret/selection so the menu appears where the user types.
  const { style, ...rest } = props as BeautifulMentionsMenuProps & {
    style?: React.CSSProperties;
  };
  const incomingStyle = (style || {}) as Record<string, unknown>;

  const [computedStyle, setComputedStyle] =
    useState<React.CSSProperties | null>(null);

  useEffect(() => {
    // Try to get a bounding rect for the caret/selection
    try {
      const sel = window.getSelection?.();
      let rect: DOMRect | null = null;

      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0).cloneRange();
        // If the range is collapsed the clientRects may still be empty; use getBoundingClientRect as fallback
        const clientRects = range.getClientRects();
        if (clientRects && clientRects.length > 0) {
          rect = clientRects[0];
        } else {
          rect = range.getBoundingClientRect();
        }
      }

      // Fallback to using the incoming style if selection rect not available
      const incomingTop = incomingStyle.top as string | number | undefined;
      const incomingLeft = incomingStyle.left as string | number | undefined;

      const baseStyle: React.CSSProperties = {};

      // preserve width if provided by plugin
      if (incomingStyle.width) {
        baseStyle.width = incomingStyle.width as string | number;
      }

      if (rect && rect.top !== 0 && rect.left !== 0) {
        // Position below the caret
        setComputedStyle({ ...baseStyle, top: rect.bottom, left: rect.left });
        return;
      }

      // If rect not useful, try to parse incoming top/left and adjust by scroll
      const parsePx = (v: string | number | undefined) =>
        typeof v === "string" && v.endsWith("px")
          ? parseFloat(v)
          : typeof v === "number"
            ? v
            : undefined;
      const t = parsePx(incomingTop);
      const l = parsePx(incomingLeft);

      if (typeof t === "number" && typeof l === "number") {
        // incoming top/left may be relative to the document; convert to viewport coords
        const finalTop = t - window.scrollY;
        setComputedStyle({ ...baseStyle, top: finalTop, left: l });
        return;
      }

      // Last resort: place near top-left of editor
      setComputedStyle({ ...baseStyle, top: 0, left: 0 });
    } catch (e) {
      // swallow errors and fallback to incoming style
      setComputedStyle({
        ...(incomingStyle as React.CSSProperties),
        position: "fixed",
        zIndex: 99999,
      });
    }
    // Run this effect once when the menu mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Portal>
      <ul
        {...rest}
        className={styles.mentionMenu}
        style={computedStyle ?? {}}
      />
    </Portal>
  );
}

const MenuItem = forwardRef<HTMLLIElement, BeautifulMentionsMenuItemProps>(
  ({ selected, item, ...props }, ref) => (
    <li
      ref={ref}
      {...props}
      className={[
        styles.mentionMenuItem,
        selected && styles.mentionMenuItemSelected,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className={styles.mentionTrigger}>{item.trigger}</span>
      <span className={styles.mentionValue}>{item.value}</span>
    </li>
  ),
);

interface CustomOutputProps {
  placeholderText?: string;
  labelText?: string;
  required?: boolean;
  mentionItems: Record<string, string[]>;
  error?: ReactNode;
  // Controlled props from form getInputProps
  value?: string;
  onChange?: (value: string) => void;
}

export default function CustomOutput({
  placeholderText,
  labelText,
  required = false,
  mentionItems,
  error = "",
  value,
  onChange,
}: CustomOutputProps) {
  // Transform mention items to replace dots with hyphens for display (hyphens are often treated as word characters)
  const transformedMentionItems = transformMentionItemsForDisplay(mentionItems);
  const hasError = (() => {
    if (error === null || error === undefined) return false;
    if (typeof error === "string") return error.trim().length > 0;
    if (typeof error === "boolean") return false;
    return true;
  })();
  // Transform value from storage format {variable.name} to display format :variable-name
  const transformedValue = transformOutputForDisplay(value || "");

  const initialConfig = {
    namespace: "CustomOutput",
    nodes: [BeautifulMentionNode],
    theme: {
      beautifulMentions: buildBeautifulMentionsTheme(transformedMentionItems),
    },
    onError: (e: unknown) => console.error(e),
  };

  return (
    <LexicalComposer
      initialConfig={{
        ...initialConfig,
        editorState: () => {
          try {
            $convertFromMarkdownString(transformedValue || "", TRANSFORMERS);
          } catch (error) {
            console.error(
              `Error parsing initial value: ${transformedValue}`,
              error,
            );
          }

          // Transform text to mention nodes using the triggers from transformed mentionItems
          const triggers = Object.keys(transformedMentionItems);

          // Use the built-in transform with space-separated values
          $transformTextToMentionNodes(triggers);
        },
      }}
    >
      <Box miw={{ base: "auto", lg: "400px" }} w="100%">
        <Box style={{ position: "relative" }}>
          {labelText && (
            <Flex>
              <Text size="sm" mb={4} variant={hasError ? "carrot" : "grey-100"}>
                {labelText}
              </Text>
              {required && (
                <Text size="sm" variant="green" mb={4} ml={3}>
                  *
                </Text>
              )}
            </Flex>
          )}
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={[
                  styles.editorWrapper,
                  hasError && styles.editorWrapperError,
                ]
                  .filter(Boolean)
                  .join(" ")}
              />
            }
            placeholder={
              <Text
                size="sm"
                fs="italic"
                variant="grey-700"
                className={styles.placeholder}
              >
                {placeholderText}
              </Text>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </Box>
        {hasError && (
          <Text mt={4} size="xs" variant="carrot">
            {error}
          </Text>
        )}
      </Box>
      <HistoryPlugin />
      {/* Plugins to keep the editor in sync with controlled value and notify on changes */}
      {onChange && <EditorChangePlugin onChange={onChange} />}
      {typeof value === "string" && (
        <EditorValueSync
          value={transformedValue}
          mentionTriggers={Object.keys(transformedMentionItems)}
        />
      )}
      <BeautifulMentionsPlugin
        items={transformedMentionItems}
        menuComponent={Menu}
        menuItemComponent={MenuItem}
        menuItemLimit={false}
        punctuation=""
        allowSpaces={false}
      />
    </LexicalComposer>
  );
}

function EditorChangePlugin({
  onChange,
}: {
  onChange: (value: string) => void;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor || !onChange) return;
    const remove = editor.registerUpdateListener(({ editorState }) => {
      try {
        editorState.read(() => {
          const root = $getRoot();
          const text = root.getTextContent();
          // Pass the raw text as-is, transformation happens only on save
          onChange(text);
        });
      } catch (e) {
        // swallow
      }
    });

    return () => remove();
  }, [editor, onChange]);

  return null;
}

function EditorValueSync({
  value,
  mentionTriggers,
}: {
  value: string;
  mentionTriggers: string[];
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor) return;
    try {
      editor.update(() => {
        const root = $getRoot();
        // If the editor already has the same text, skip
        const current = root.getTextContent();
        if (current === value) return;

        root.clear();
        const paragraph = $createParagraphNode();
        paragraph.append($createTextNode(value));
        root.append(paragraph);

        // Transform mentions after setting the text
        $transformTextToMentionNodes(mentionTriggers);
      });
    } catch (e) {
      console.error("Error in EditorValueSync:", e);
    }
  }, [editor, value, mentionTriggers]);

  return null;
}
